'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { msLeft, type PendingApprovalBook } from './pending-approvals';

export { msLeft, formatCountdown, type PendingApprovalBook } from './pending-approvals';

// Set right before sending the shopper to Shopify checkout (see BagDrawer's
// onCheckout) and read back here. The pending-approvals row this banner needs
// is created by the order-paid webhook, which can lag a few seconds behind
// Shopify's post-purchase redirect — so a shopper who lands back on the home
// page right after paying can hit an empty result on the very first fetch.
// This flag tells the hook it's worth retrying instead of taking that empty
// result as final. Cleared once a book shows up or the retry window lapses.
const CHECKOUT_MARKER_KEY = 'pwm_checkout_started_at';
const CHECKOUT_MARKER_WINDOW_MS = 15 * 60 * 1000; // how long a marker is considered "recent"
// Exported: /comix/approve's own purchase check (check-purchase) hits the identical
// order-paid-webhook-lag race right after a wallet payment redirects straight there, so
// it retries with the same backoff instead of showing "not purchased" on the first miss.
export const CHECKOUT_RETRY_DELAYS_MS = [3000, 6000, 12000, 24000]; // ~45s total, covers realistic webhook lag
const RETRY_DELAYS_MS = CHECKOUT_RETRY_DELAYS_MS;

export function markCheckoutStarted(): void {
  try {
    localStorage.setItem(CHECKOUT_MARKER_KEY, String(Date.now()));
  } catch {
    /* localStorage unavailable (private mode, etc.) — retry just won't kick in */
  }
}

export function hasRecentCheckoutMarker(): boolean {
  try {
    const raw = localStorage.getItem(CHECKOUT_MARKER_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < CHECKOUT_MARKER_WINDOW_MS;
  } catch {
    return false;
  }
}

function clearCheckoutMarker(): void {
  try {
    localStorage.removeItem(CHECKOUT_MARKER_KEY);
  } catch {
    /* nothing to clean up if we can't reach localStorage */
  }
}

/**
 * Fetches this session's printed comic books that still need approval before they
 * ship.
 *
 * - Reads the `comixSessionId` cookie; no session → no books, no request.
 * - Refetches on mount and whenever the tab regains focus / becomes visible
 *   (catches an approval done on another tab). No background polling.
 * - If a recent checkout marker is set (see markCheckoutStarted) and the fetch comes
 *   back empty, retries a few times with backoff — covers the order-paid webhook lag
 *   race described above — instead of treating that first empty result as final.
 * - Drops any book whose auto-approve deadline has since passed (the cron should
 *   have finalized it by then, at which point a refetch would drop it via
 *   `userApproved` anyway — this is just the gap until that refetch happens) —
 *   re-checked every 15s, so consumers re-render at most 4×/min from this hook. The
 *   live 1s countdown is owned by the leaf that renders it (PendingApprovalCard), not
 *   here, so the banner/popup wrappers aren't re-rendered every second.
 */
export function usePendingApprovals(): {
  books: PendingApprovalBook[];
  loading: boolean;
  refetch: () => void;
} {
  const [raw, setRaw] = useState<PendingApprovalBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [expiryTick, setExpiryTick] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);

  const refetch = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    retryCountRef.current = 0;

    const sessionId = Cookies.get('comixSessionId');
    if (!sessionId) {
      setRaw([]);
      setLoading(false);
      return;
    }

    const runFetch = () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      fetch(`/api/comix/pending-approvals?sessionId=${encodeURIComponent(sessionId)}`, {
        signal: ctrl.signal,
        cache: 'no-store',
      })
        .then((r) => (r.ok ? r.json() : { books: [] }))
        .then((d) => {
          const books = Array.isArray(d?.books) ? d.books : [];
          setRaw(books);
          if (books.length > 0) {
            clearCheckoutMarker();
            return;
          }
          if (hasRecentCheckoutMarker() && retryCountRef.current < RETRY_DELAYS_MS.length) {
            const delay = RETRY_DELAYS_MS[retryCountRef.current];
            retryCountRef.current += 1;
            retryTimerRef.current = setTimeout(runFetch, delay);
          } else {
            clearCheckoutMarker();
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    };

    runFetch();
  }, []);

  useEffect(() => {
    refetch();
    const onFocus = () => refetch();
    const onVisible = () => {
      if (document.visibilityState === 'visible') refetch();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      abortRef.current?.abort();
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refetch]);

  // Coarse timer just to re-evaluate the deadline expiry filter below.
  useEffect(() => {
    if (raw.length === 0) return;
    const id = setInterval(() => setExpiryTick((t) => t + 1), 15_000);
    return () => clearInterval(id);
  }, [raw.length]);

  const books = useMemo(() => {
    const now = Date.now();
    return raw.filter((b) => msLeft(b.deadlineAt, now) > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw, expiryTick]);

  return { books, loading, refetch };
}
